import {
  TableRow, TableCell, Grid, Typography, TextField, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, Snackbar, Box, Tooltip, IconButton, useTheme,
} from '@mui/material';
import { AddOrderItemRequest, Item, OrderDetails, OrderItemDetails, OrderUpdate, OrderlySystem, client } from '../../../../app/services/api/orderManagementClient';
import { ChangeEvent, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../hooks/useLogError';
import { UserContext } from '../../../../components/shared/useUser';
import { useNavigate } from 'react-router-dom';
import { PredictedDemandModel } from './../../../../app/services/api/podClient';
import { MagicWand01 } from './../../../../assets';
import { QuantityWarningDialog } from './../../../../components/shared/orders/QuantityWarningDialog';
import { ProductDetailsCard } from './../../../../components/shared/orders/ProductDetailsCard';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../../enums/OrderCategories';
import { isAutoShipOrder } from '../../../../enums/OrderType';
import dayjs from 'dayjs';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';
import { calculateItemParQuantity, getInventorySku } from '../../../../utils';

type ProductRowProps = {
  product: Item;
  activeOrder?: OrderDetails;
  selectedOrderId?: number;
  orderItemId: number;
  basketQuantity?: number;
  matchedOrderItem?: OrderItemDetails;
  isParEnabled?: boolean;
  onAddItemConfirmed: () => void | Promise<void>;
  onDeleteItemConfirmed: () => void | Promise<void>;
  currencyCode?: string;
  showMessageWhenNoCost: boolean;
  predictedItem?: PredictedDemandModel;
  showPredictedQuantity?: boolean;
  canEditPredictions?: boolean;
  orderType: number;
  deliveryDate: dayjs.Dayjs | null;
}

const parseDraftQuantity = (value: string | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const ProductRow = forwardRef<{ matchPredictedQuantities: () => void } | null, ProductRowProps>(({
  product,
  activeOrder,
  selectedOrderId,
  orderItemId,
  basketQuantity,
  matchedOrderItem,
  isParEnabled = false,
  currencyCode,
  onAddItemConfirmed,
  onDeleteItemConfirmed,
  showMessageWhenNoCost,
  orderType,
  predictedItem,
  showPredictedQuantity,
  canEditPredictions = true,
  deliveryDate,

}, ref) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('products');
  const { t: tOrderDetails } = useTranslation('orderDetails');
  const { logError } = useLogError();
  const { selectedStore } = useContext(UserContext);
  const [quantity, setQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [startingQuantity, setStartingQuantity] = useState<string>(basketQuantity?.toString() ?? '0');
  const [loading, setLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const isFocused = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));
  const predictedQuantity = predictedDemandItem?.quantity.toString();
  const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
  const borderColor = showPrediction && predictedQuantity === quantity ? theme.palette.success[400] : '';
  const minStockOnHandQty = predictedDemandItem?.minStockOnHandQty ?? predictedDemandItem?.safetyStock;
  const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
  const currentQuantity = Number(quantity || '0');
  const fallbackParQuantity = isParEnabled ? calculateItemParQuantity(product) : null;
  const parOwnedQuantity = matchedOrderItem?.calculatedParQuantity ?? matchedOrderItem?.parQuantityApplied;
  const displayedParQuantity = parOwnedQuantity ?? fallbackParQuantity;
  const isParOwnedItem = isParEnabled
    && matchedOrderItem?.isQuantityOverridden !== true
    && displayedParQuantity != null
    && currentQuantity === displayedParQuantity;
  const isPersistedParOwnedItem = isParEnabled
    && displayedParQuantity != null
    && matchedOrderItem?.isQuantityOverridden !== true
    && (matchedOrderItem ? matchedOrderItem.quantity === displayedParQuantity : true);
  const getDisplayedQuantity = () => {
    const basketValue = basketQuantity?.toString() ?? '0';

    if (!isParEnabled) {
      return basketValue;
    }

    if (matchedOrderItem?.isQuantityOverridden === true) {
      return basketValue;
    }

    if (displayedParQuantity != null) {
      return displayedParQuantity.toString();
    }

    return basketValue;
  };

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (isParEnabled) {
      return;
    }

    setQuantity(basketQuantity?.toString() ?? '0');
    setStartingQuantity(basketQuantity?.toString() ?? '0');
  }, [basketQuantity, isParEnabled]);

  useEffect(() => {
    if (!isParEnabled) {
      return;
    }

    if (isFocused.current || loading) {
      return;
    }

    const nextQuantity = getDisplayedQuantity();
    setQuantity(nextQuantity);
    setStartingQuantity(nextQuantity);
  }, [basketQuantity, displayedParQuantity, isParEnabled, loading, matchedOrderItem?.isQuantityOverridden]);

  useEffect(() => {
    if (!isParEnabled) {
      return undefined;
    }

    const element = inputRef.current;

    if (!element) {
      return undefined;
    }

    const handleWheel = (event: WheelEvent) => {
      if (document.activeElement === element) {
        event.preventDefault();
      }
    };

    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [isParEnabled]);

  const onQuantityChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.preventDefault();

    if (!isParEnabled) {
      const value = Math.abs(Number(event.target.value || '0'));
      setQuantity(value.toString());
      return;
    }

    const rawValue = event.target.value;

    if (rawValue === '') {
      setQuantity('');
      return;
    }

    const parsedValue = parseDraftQuantity(rawValue);

    if (parsedValue == null) {
      return;
    }

    let nextQuantity = parsedValue;

    if (nextQuantity < 0) {
      nextQuantity = 0;
    }

    if (rawValue.includes('.')) {
      nextQuantity = Math.round(nextQuantity);
    }

    setQuantity(nextQuantity.toString());
  };

  const handleMatchQuantityChange = () => {
    setQuantity(predictedQuantity?.toString() ?? '');
  };

  useImperativeHandle(ref, () => ({
    matchPredictedQuantities,
  }));

  const matchPredictedQuantities = () => {
    if (showPrediction) {
      setQuantity(predictedQuantity ?? quantity);
    }
  };

  const onAddItemToOrder = async (itemId: number, quantity: number) => {
    if (selectedOrderId) {
      try {
        setLoading(true);
        const request: AddOrderItemRequest = {
          orderId: selectedOrderId,
          itemId,
          quantity,
          predictedQuantity: predictedDemandItem?.quantity,
        };
        const response = await client.addOrderItem(selectedStore?.storeNumber, request);
        if (response.success) {
          handleSnackbarOpen(t('addItemSuccessMessage'), 'success');
          if (isParEnabled) {
            setQuantity(quantity.toString());
            setStartingQuantity(quantity.toString());
            await onAddItemConfirmed();
          } else {
            setQuantity('');
            setStartingQuantity(quantity.toString());
            void onAddItemConfirmed();
          }
        }
        else {
          setWarningMessage(response.message);
          setShowQuantityWarningDialog(true);
          fixQuantity();
        }
      } catch (error) {
        handleSnackbarOpen(t('addItemErrorMessage') + ' ' + error, 'error');
        logError(error);
      } finally {
        setLoading(false);
        setShowDialog(false);
      }
    }
  };

  const handleDeleteOrderItem = async () => {
    if (selectedOrderId && orderItemId) {
      setLoading(true);
      try {
        const result = await client.deleteOrderItem(selectedStore?.storeNumber, selectedOrderId.toString(), orderItemId.toString());
        if (result.success) {
          handleSnackbarOpen(tOrderDetails('deleteSuccess'), 'success');
          if (isParEnabled) {
            await onDeleteItemConfirmed();
          } else {
            void onDeleteItemConfirmed();
          }
        } else {
          handleSnackbarOpen(tOrderDetails('deleteItemFail'), 'error');
        }
      } catch (error) {
        handleSnackbarOpen(tOrderDetails('deleteItemFail') + ' ' + error, 'error');
      } finally {
        setLoading(false);
        setShowDialog(false);
      }
    }

  };

  const handleResetToPar = async () => {
    const resetQuantity = displayedParQuantity?.toString() ?? '0';
    const previousStartingQuantity = startingQuantity;

    if (!isParEnabled) {
      setQuantity(startingQuantity);
      return;
    }

    if (isPersistedParOwnedItem || !activeOrder || !selectedOrderId || !matchedOrderItem) {
      setQuantity(resetQuantity);
      setStartingQuantity(resetQuantity);
      setShowDialog(false);
      return;
    }

    try {
      setQuantity(resetQuantity);
      setStartingQuantity(resetQuantity);
      setLoading(true);

      const updateRequest: OrderUpdate = {
        id: activeOrder.id,
        orderlySystem: OrderlySystem.OFS,
        items: activeOrder.items.map((item) => ({
          id: item.itemId,
          quantity: item.itemId === product.id ? null : item.quantity,
        })),
      };

      const result = await client.updateOrder(selectedStore?.storeNumber, updateRequest);

      if (result.success) {
        handleSnackbarOpen(tOrderDetails('updateSuccess'), 'success');
        await onAddItemConfirmed();
      } else {
        handleSnackbarOpen(result.message || tOrderDetails('updateError'), 'error');
        setQuantity(previousStartingQuantity);
        setStartingQuantity(previousStartingQuantity);
      }
    } catch (error) {
      handleSnackbarOpen(tOrderDetails('updateError') + ' ' + error, 'error');
      logError(error);
      setQuantity(previousStartingQuantity);
      setStartingQuantity(previousStartingQuantity);
    } finally {
      setLoading(false);
      setShowDialog(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hideDialogAndResetQuantity = () => {
    setShowDialog(false);
    setQuantity(startingQuantity);
  };

  const quantityError = (): string | null => {
    if (!isParEnabled) {
      if (Number(quantity) == 0) {
        return null;
      }

      if ((product.maxQuantity != null) && Number(quantity) > product.maxQuantity)
        return `${t('maxQuantityAllowedIs')}: ${product.maxQuantity}`;

      if ((product.minQuantity != null && Number(quantity) > 0) && Number(quantity) < product.minQuantity)
        return `${t('minQuantityAllowedIs')}: ${product.minQuantity}`;

      return null;
    }

    const parsedQuantity = parseDraftQuantity(quantity);

    if (parsedQuantity == null || parsedQuantity === 0) {
      return null;
    }

    if ((product.maxQuantity != null) && parsedQuantity > product.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${product.maxQuantity}`;

    if ((product.minQuantity != null && parsedQuantity > 0) && parsedQuantity < product.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${product.minQuantity}`;

    return null;
  };

  const fixQuantity = () => {
    if (!isParEnabled) {
      if (Number(quantity) != 0) {
        if (Number(quantity) > product.maxQuantity)
          setQuantity(product.maxQuantity.toString());
        if (Number(quantity) < product.minQuantity)
          setQuantity(product.minQuantity.toString());
      }
      return;
    }

    const parsedQuantity = parseDraftQuantity(quantity);

    if (parsedQuantity != null && parsedQuantity !== 0) {
      if (product.maxQuantity != null && parsedQuantity > product.maxQuantity)
        setQuantity(product.maxQuantity.toString());
      if (product.minQuantity != null && parsedQuantity < product.minQuantity)
        setQuantity(product.minQuantity.toString());
    }
  };

  const onUpdateBtnClick = async (productId: number, nextQuantity: number | null) => {
    if (!isParEnabled) {
      if ((nextQuantity ?? 0) > 0) {
        await onAddItemToOrder(productId, nextQuantity ?? 0);
      } else {
        await handleDeleteOrderItem();
      }
      return;
    }

    if (nextQuantity == null) {
      await handleResetToPar();
      return;
    }

    await onAddItemToOrder(productId, nextQuantity);
  };

  return (
    <>
      <TableRow key={product.id}>
        <TableCell sx={{ width: '60%' }}>
          <Box sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/store/item/${product.id}?sku=${encodeURIComponent(product.useInventorySKU ? product.gtin : product.sku)}&orderable=true`)}>
            <ProductDetailsCard item={product}
              currencyCode={currencyCode}
              showMessageWhenNoCost={showMessageWhenNoCost}
              categoryLabel={isOrderCategory(product.category)
                ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                : product.category}
              parText={isParEnabled && displayedParQuantity != null
                ? tOrderDetails('parLevel', { value: displayedParQuantity })
                : null} />
          </Box>
        </TableCell>
        <TableCell>
          <Box className="product-quantity-container"
            sx={{ display: 'flex', position: 'relative', maxWidth: 100 }}>
            <TextField
              label={showPrediction && canEditPredictions
                ? t('predictedQuantity', { value: predictedQuantity })
                : isParOwnedItem
                  ? tOrderDetails('parLabel')
                  : null}
              error={(quantityError()?.length ?? 0) > 0}
              helperText={quantityError()}
              size='small'
              type='number'
              autoComplete='off'
              placeholder={isParEnabled && quantity === '' && matchedOrderItem?.isQuantityOverridden !== true && displayedParQuantity != null ? displayedParQuantity.toString() : undefined}
              inputRef={isParEnabled ? inputRef : undefined}
              inputProps={isParEnabled ? { min: 0, step: 1 } : undefined}
              sx={{
                maxWidth: 100,
                width: '100%',
                input: {
                  textAlign: 'center',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: borderColor,
                  },
                },

                '& .MuiInputLabel-root': {
                  color: borderColor,
                },
              }}
              disabled={!selectedOrderId || isAutoShipOrder(orderType) || (showPrediction && !canEditPredictions)}
              value={quantity}
              onFocus={isParEnabled ? (event) => {
                isFocused.current = true;
                event.target.select();
              } : undefined}
              onBlur={isParEnabled ? () => {
                isFocused.current = false;
              } : undefined}
              onChange={(e) => onQuantityChange(e)}
            ></TextField>
            {
              showPrediction && canEditPredictions &&
              <Tooltip title={
                <span>
                  {
                    predictedDemandQuantity != null && (
                      <>
                        {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: product.primaryUom })}
                        <br />
                      </>
                    )
                  }
                  {minStockOnHandQty != null && t('safetyStock', { value: minStockOnHandQty, uom: product.primaryUom })}
                </span>
              }>
                <IconButton aria-label={t('applyPredictedQuantity')}
                  disabled={!canEditPredictions}
                  onClick={() => handleMatchQuantityChange()}>
                  <MagicWand01
                    color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
                </IconButton>
              </Tooltip>
            }
          </Box>
        </TableCell>
        <TableCell>
          <Button
            disabled={!selectedOrderId || isAutoShipOrder(orderType) || (quantityError()?.length ?? 0) > 0}
            variant="primary"
            onClick={() => setShowDialog(true)}
            size="lg"
          >
            {t('updateQuantity')}
          </Button>
        </TableCell>
      </TableRow>
      <Dialog open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('confirmDialogTitle')}
          <CloseDialogAction onCancel={() => setShowDialog(false)} />
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>{t('confirmDialogContent', { item: product.name })}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions >
          <Button
            variant="secondary"
            onClick={hideDialogAndResetQuantity}>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={() => onUpdateBtnClick(product.id, isParEnabled && quantity === '' ? null : Number(quantity))}
            disabled={loading || isAutoShipOrder(orderType)}>
            {t('update')}
          </Button>
        </DialogActions>
      </Dialog>
      <QuantityWarningDialog setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

ProductRow.displayName = 'ProductRow';

export default ProductRow;